// Enums para tipos de usuário e permissões
export enum UserRole {
  ADMIN = 'admin',
  PET_OWNER = 'pet_owner',
  VETERINARIAN = 'veterinarian'
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  BLOCKED = 'blocked'
}

export enum Permission {
  // Permissões de Administrador
  MANAGE_USERS = 'manage_users',
  MANAGE_VETERINARIANS = 'manage_veterinarians',
  MANAGE_PETS = 'manage_pets',
  MANAGE_APPOINTMENTS = 'manage_appointments',
  MANAGE_SYSTEM = 'manage_system',
  VIEW_ANALYTICS = 'view_analytics',
  MANAGE_CONTENT = 'manage_content',

  // Permissões de Dono de Pet
  CREATE_PET = 'create_pet',
  EDIT_OWN_PET = 'edit_own_pet',
  VIEW_OWN_PETS = 'view_own_pets',
  DELETE_OWN_PET = 'delete_own_pet',
  SCHEDULE_APPOINTMENT = 'schedule_appointment',
  VIEW_OWN_APPOINTMENTS = 'view_own_appointments',
  CANCEL_OWN_APPOINTMENT = 'cancel_own_appointment',

  // Permissões de Veterinário
  VIEW_ASSIGNED_APPOINTMENTS = 'view_assigned_appointments',
  MANAGE_ASSIGNED_APPOINTMENTS = 'manage_assigned_appointments',
  VIEW_PATIENT_HISTORY = 'view_patient_history',
  CREATE_PRESCRIPTION = 'create_prescription',
  EDIT_OWN_PROFILE = 'edit_own_profile',
  MANAGE_AVAILABILITY = 'manage_availability'
}

// Interface base do usuário
export interface BaseUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  permissions: Permission[];
  createdAt: Date;
  updatedAt: Date;
  created_at: string; // Vindo da API
  updated_at: string; // Vindo da API
  lastLoginAt?: Date;
  avatar?: string;
  photo_url?: string;
  years_experience?: number;
  verification_status?: string;
  specialties?: string[];
  veterinarian_id?: string;
  crmv?: string;
  phone?: string;
  location?: string;
  bio?: string;
  street?: string;
  number?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

// Interface para administrador
export interface AdminUser extends BaseUser {
  role: UserRole.ADMIN;
  adminLevel: 'super' | 'standard';
  managedSections: string[];
}

// Interface para dono de pet (como o João)
export interface PetOwnerUser extends BaseUser {
  role: UserRole.PET_OWNER;
  phone: string;
  cpf: string;
  dateOfBirth: Date;
  address: {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
  };
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  petIds: string[];
  subscriptionId?: string;
  preferences: {
    notifications: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
    language: string;
    timezone: string;
  };
}

// Interface para veterinário
export interface VeterinarianUser extends BaseUser {
  role: UserRole.VETERINARIAN;
  crmv: string;
  phone: string;
  specialties: string[];
  bio: string;
  yearsExperience: number;
  education: string;
  languages: string[];
  consultationFee: {
    min: number;
    max: number;
  };
  availability: {
    monday: string[];
    tuesday: string[];
    wednesday: string[];
    thursday: string[];
    friday: string[];
    saturday: string[];
    sunday: string[];
  };
  location: string;
  rating: number;
  totalReviews: number;
  isAvailable: boolean;
  verificationStatus: 'pending' | 'verified' | 'rejected';
  documents: {
    crmvDocument?: string;
    diplomaDocument?: string;
    identityDocument?: string;
  };
}

// Union type para todos os tipos de usuário
export type User = AdminUser | PetOwnerUser | VeterinarianUser;

// Interface para criação de usuários
export interface CreateUserDto {
  email: string;
  password: string;
  name: string;
  role: UserRole;
}

export interface CreatePetOwnerDto extends CreateUserDto {
  role: UserRole.PET_OWNER;
  phone: string;
  cpf: string;
  dateOfBirth: string;
  address: {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

export interface CreateVeterinarianDto extends CreateUserDto {
  role: UserRole.VETERINARIAN;
  crmv: string;
  phone: string;
  specialties: string[];
  bio: string;
  yearsExperience: number;
  education: string;
  consultationFeeMin: number;
  consultationFeeMax: number;
  location: string;
}

export interface CreateAdminDto extends CreateUserDto {
  role: UserRole.ADMIN;
  adminLevel: 'super' | 'standard';
  managedSections: string[];
}

// Sistema de permissões por role
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: [
    Permission.MANAGE_USERS,
    Permission.MANAGE_VETERINARIANS,
    Permission.MANAGE_PETS,
    Permission.MANAGE_APPOINTMENTS,
    Permission.MANAGE_SYSTEM,
    Permission.VIEW_ANALYTICS,
    Permission.MANAGE_CONTENT
  ],
  [UserRole.PET_OWNER]: [
    Permission.CREATE_PET,
    Permission.EDIT_OWN_PET,
    Permission.VIEW_OWN_PETS,
    Permission.DELETE_OWN_PET,
    Permission.SCHEDULE_APPOINTMENT,
    Permission.VIEW_OWN_APPOINTMENTS,
    Permission.CANCEL_OWN_APPOINTMENT
  ],
  [UserRole.VETERINARIAN]: [
    Permission.VIEW_ASSIGNED_APPOINTMENTS,
    Permission.MANAGE_ASSIGNED_APPOINTMENTS,
    Permission.VIEW_PATIENT_HISTORY,
    Permission.CREATE_PRESCRIPTION,
    Permission.EDIT_OWN_PROFILE,
    Permission.MANAGE_AVAILABILITY
  ]
};

// Interface para sessão do usuário
export interface UserSession {
  user: User;
  token: string;
  expiresAt: Date;
  permissions: Permission[];
}

// Interface para contexto de autenticação
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: Permission[];
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasRole: (role: UserRole) => boolean;
  updateProfile: (data: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
}

// DTOs para atualização de perfil
export interface UpdatePetOwnerProfileDto {
  name?: string;
  phone?: string;
  address?: Partial<PetOwnerUser['address']>;
  emergencyContact?: PetOwnerUser['emergencyContact'];
  preferences?: Partial<PetOwnerUser['preferences']>;
  avatar?: string;
}

export interface UpdateVeterinarianProfileDto {
  name?: string;
  phone?: string;
  bio?: string;
  specialties?: string[];
  languages?: string[];
  consultationFee?: {
    min: number;
    max: number;
  };
  availability?: Partial<VeterinarianUser['availability']>;
  location?: string;
  avatar?: string;
}

export interface UpdateAdminProfileDto {
  name?: string;
  avatar?: string;
  managedSections?: string[];
}

// Mock data para usuários
export const MOCK_USERS: Record<string, User> = {
  'admin-1': {
    id: 'admin-1',
    email: 'admin@pulsepet.com',
    name: 'Administrador Sistema',
    role: UserRole.ADMIN,
    status: UserStatus.ACTIVE,
    permissions: ROLE_PERMISSIONS[UserRole.ADMIN],
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date(),
    lastLoginAt: new Date(),
    adminLevel: 'super',
    managedSections: ['users', 'veterinarians', 'pets', 'appointments', 'system']
  } as AdminUser,
  'user-1': {
    id: 'user-1',
    email: 'joao@email.com',
    name: 'João Silva',
    role: UserRole.PET_OWNER,
    status: UserStatus.ACTIVE,
    permissions: ROLE_PERMISSIONS[UserRole.PET_OWNER],
    createdAt: new Date('2024-06-15'),
    updatedAt: new Date(),
    lastLoginAt: new Date(),
    phone: '(11) 99999-8888',
    cpf: '123.456.789-00',
    dateOfBirth: new Date('1985-03-20'),
    address: {
      street: 'Rua das Flores',
      number: '123',
      complement: 'Apto 45',
      neighborhood: 'Vila Madalena',
      city: 'São Paulo',
      state: 'SP',
      zipCode: '05433-000'
    },
    emergencyContact: {
      name: 'Maria Silva',
      phone: '(11) 98888-7777',
      relationship: 'Esposa'
    },
    petIds: ['1', '2'],
    preferences: {
      notifications: {
        email: true,
        sms: true,
        push: true
      },
      language: 'pt-BR',
      timezone: 'America/Sao_Paulo'
    }
  } as PetOwnerUser,
  'vet-1': {
    id: 'vet-1',
    email: 'maria.silva@pulsepet.com',
    name: 'Dra. Maria Silva',
    role: UserRole.VETERINARIAN,
    status: UserStatus.ACTIVE,
    permissions: ROLE_PERMISSIONS[UserRole.VETERINARIAN],
    createdAt: new Date('2024-03-10'),
    updatedAt: new Date(),
    lastLoginAt: new Date(),
    crmv: 'CRMV-SP 12345',
    phone: '(11) 99999-1234',
    specialties: ['Clínico Geral', 'Cardiologia'],
    bio: 'Veterinária com 15 anos de experiência em clínica geral e cardiologia veterinária.',
    yearsExperience: 15,
    education: 'Medicina Veterinária - Universidade de São Paulo (2009)',
    languages: ['Português', 'Inglês'],
    consultationFee: { min: 120, max: 180 },
    availability: {
      monday: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'],
      tuesday: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'],
      wednesday: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'],
      thursday: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'],
      friday: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'],
      saturday: ['09:00', '10:00', '11:00'],
      sunday: []
    },
    location: 'São Paulo, SP',
    rating: 4.9,
    totalReviews: 127,
    isAvailable: true,
    verificationStatus: 'verified',
    documents: {}
  } as VeterinarianUser
};